import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, CalendarDays, Clock, Images, LogOut, Flower2, Menu, ExternalLink, Newspaper, GalleryHorizontalEnd, Sparkles, FileText, HeartHandshake, Mail, Settings, HandCoins, KeyRound, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";

const groups = [
  { heading: null, items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" }] },
  { heading: "Content", items: [
    { to: "/admin/content", label: "Site Content", icon: Settings, testid: "admin-nav-content" },
    { to: "/admin/banners", label: "Banners", icon: GalleryHorizontalEnd, testid: "admin-nav-banners" },
    { to: "/admin/news", label: "News", icon: Newspaper, testid: "admin-nav-news" },
    { to: "/admin/programs", label: "Activities", icon: Sparkles, testid: "admin-nav-programs" },
    { to: "/admin/gallery", label: "Gallery", icon: Images, testid: "admin-nav-gallery" },
    { to: "/admin/documents", label: "Documents", icon: FileText, testid: "admin-nav-documents" },
  ]},
  { heading: "Programs", items: [
    { to: "/admin/events", label: "Events", icon: CalendarDays, testid: "admin-nav-events" },
    { to: "/admin/schedules", label: "Ashram Schedule", icon: Clock, testid: "admin-nav-schedules" },
  ]},
  { heading: "Community", items: [
    { to: "/admin/trustees", label: "Trustees", icon: Landmark, testid: "admin-nav-trustees" },
    { to: "/admin/members", label: "Members", icon: Users, testid: "admin-nav-members" },
    { to: "/admin/volunteers", label: "Volunteers", icon: HeartHandshake, testid: "admin-nav-volunteers" },
    { to: "/admin/enquiries", label: "Enquiries", icon: Mail, testid: "admin-nav-enquiries" },
  ]},
  { heading: "Finance", items: [
    { to: "/admin/donations", label: "Donations", icon: HandCoins, testid: "admin-nav-donations" },
  ]},
  { heading: "Account", items: [
    { to: "/admin/change-password", label: "Change Password", icon: KeyRound, testid: "admin-nav-change-password" },
  ]},
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => setOpen(false), [location.pathname]);
  const handleLogout = () => { logout(); navigate("/admin/login"); };

  const Sidebar = (
    <div className="w-64 bg-[#0E2916] text-white flex flex-col justify-between shrink-0 h-full">
      <div className="overflow-y-auto">
        <div className="flex items-center gap-3 px-6 h-20 border-b border-white/10 sticky top-0 bg-[#0E2916]">
          <div className="h-10 w-10 rounded-full bg-[#194324] flex items-center justify-center"><Flower2 className="h-5 w-5 text-[#C59B27]" /></div>
          <div className="leading-tight"><div className="font-cinzel font-bold text-sm text-[#C59B27]">HATHA VIDYA</div><div className="text-[10px] text-white/50 uppercase tracking-widest font-mono">Admin Panel</div></div>
        </div>
        <nav className="p-4 space-y-1">
          {groups.map((g, gi) => (
            <div key={gi} className="pb-1">
              {g.heading && <div className="px-4 pt-3 pb-1 text-[10px] font-mono uppercase tracking-widest text-white/40">{g.heading}</div>}
              {g.items.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
                  className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-[#C59B27] text-[#0E2916]" : "text-white/75 hover:bg-white/10 hover:text-white"}`}>
                  <n.icon className="h-4 w-4" />{n.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </div>
      <div className="p-4 border-t border-white/10 space-y-2 bg-[#0E2916]">
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-2 text-xs text-white/60 hover:text-[#C59B27]"><ExternalLink className="h-3.5 w-3.5" /> View Public Site</a>
        <div className="px-4 text-xs text-white/50 truncate">{user?.email}</div>
        <Button onClick={handleLogout} variant="ghost" className="w-full justify-start text-white/80 hover:bg-white/10 hover:text-white" data-testid="admin-logout-button"><LogOut className="h-4 w-4 mr-2" /> Logout</Button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#F4F1EA]">
      <div className="hidden md:block fixed inset-y-0 left-0 z-40">{Sidebar}</div>
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative z-10">{Sidebar}</div>
        </div>
      )}
      <div className="flex-1 min-w-0 md:ml-64 flex flex-col">
        <header className="md:hidden flex items-center justify-between h-16 px-4 bg-[#0E2916] text-white">
          <button onClick={() => setOpen(true)} data-testid="admin-mobile-menu"><Menu className="h-6 w-6" /></button>
          <span className="font-cinzel text-[#C59B27] font-bold">ADMIN</span>
          <button onClick={handleLogout}><LogOut className="h-5 w-5" /></button>
        </header>
        <div className="flex-1 p-5 md:p-10 overflow-y-auto"><Outlet /></div>
      </div>
    </div>
  );
}
